import {
  APIListValuesRequest,
  APIListValuesResponse,
  APIPaginatedRequest,
  DeleteRequest,
  ExportRequest,
  getFetcher,
  patchFetcher,
  postFetcher,
} from "shared/api/api";
import client from "shared/api/axios";
import { createURL } from "shared/api/utils";
import { Vehicle, VEHICLES_BASE_URL } from "shared/api/vehicles/api";
import { MileageUnit } from "shared/types";

const FAILURE_MODES_BASE_ROUTE = "failureModes";

export enum RiskLabel {
  LOW = "Low",
  MEDIUM = "Medium",
  HIGH = "High",
}

export enum FailureModeStatus {
  ACTIVE = "active",
  ARCHIVED = "archived",
  UNDER_REVIEW = "under_review",
  IN_DEVELOPMENT = "in_development",
}

export interface FailureMode {
  ID: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  staleInference: boolean;
  type: "consumable" | "durable" | null;
  vehiclesFilter: string;
  failuresRepairsFilter: string | null;
  proactiveRepairsCustomRecordsFilter: string | null;
  failuresSignalEventOccurrencesFilter: string | null;
  proactiveRepairsSignalEventOccurrencesFilter: string | null;
  proactiveRepairsInspectionsFilter: string | null;
  repeatFailureAllowed: boolean;
  recallRepairCost: number | null;
  reactiveRepairCost: number | null;
  currencyCode: "USD" | "EUR" | null;
  failuresClaimsFilter: string | null;
  proactiveRepairsRepairsFilter: string | null;
  status: FailureModeStatus;
  failuresInspectionsFilter: string | null;
  failuresCustomRecordsFilter: string | null;
  proactiveRepairsClaimsFilter: string | null;
  deduplicateEvents: boolean;
  deduplicateWindow: number | null;
  deduplicateType: string | null;
  deduplicateUnit: string | null;
  proactiveRepairCost: number | null;
  inferenceRunDate: string | null;
  inspectionCost: number | null;
  warrantyPolicyID: string | null;
  warrantyPolicy: WarrantyPolicy | null;
  uncategorizedRepairsClaimsFilter: string | null;
  uncategorizedRepairsSignalEventOccurrencesFilter: string | null;
  uncategorizedRepairsInspectionsFilter: string | null;
  uncategorizedRepairsRepairsFilter: string | null;
  uncategorizedRepairsCustomRecordsFilter: string | null;
}

interface WarrantyPolicy {
  ID: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  defaultMonths: number;
  defaultMileage: number;
  defaultEngineHours: number;
  defaultMileageUnit: string;
}

export interface ListFailureModesRequest extends APIPaginatedRequest {
  skipRequest?: boolean;
}

export interface GetFailureModeRequest {
  id: string;
  mileageUnit?: MileageUnit;
}

export const listFailureModes = (args: ListFailureModesRequest) =>
  getFetcher<FailureMode[]>(listFailureModesRequestURI(args));

export const listFailureModesRequestURI = (
  params: ListFailureModesRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([FAILURE_MODES_BASE_ROUTE]),
    params,
  });

export const getFailureMode = (args: GetFailureModeRequest) =>
  getFetcher<FailureMode>(getFailureModeRequestURI(args));

export const getFailureModeRequestURI = ({
  id,
  ...params
}: GetFailureModeRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([FAILURE_MODES_BASE_ROUTE, id]),
    params,
  });

export const countFailureModesRequestURI = (
  params: ListFailureModesRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([FAILURE_MODES_BASE_ROUTE, "count"]),
    params,
  });

// values
export const listFailureModesValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listFailureModesValuesRequestURI(args));

export const getSurvivalCurveRequestURI = ({
  failureModeID,
  ...params
}: GetSurvivalCurvesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([FAILURE_MODES_BASE_ROUTE, failureModeID, "survivalCurve"]),
    params,
  });

// A failure mode without a linked risk model or survival curve returns 404 by
// design; the UI probes these endpoints and renders empty states instead, so
// don't report those 404s to Sentry.
export const getFetcherWithout404Reporting = <Data extends object>(
  path: string
) =>
  getFetcher<Data>(path, {
    skipSentryReportStatuses: [404],
  });

const listFailureModesValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([FAILURE_MODES_BASE_ROUTE, "values", fieldName]),
    params,
  });

export const getFailureModesExport = (args: ExportRequest) =>
  getFetcher<Blob>(getFailureModesExportRequestURI(args), {
    responseType: "blob",
  });

const getFailureModesExportRequestURI = ({
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([FAILURE_MODES_BASE_ROUTE, "export", type]),
    params,
  });

export interface RiskModel {
  AUC: number | null;
  ID: string;
  createdAt: string;
  createdBy: string;
  currencyCode: "USD" | "EUR" | null;
  expectedCost: number | null;
  expectedCostDenominator: number | null;
  expectedCostSaved: number | null;
  nominalFalseNegative: number | null;
  nominalFalsePositive: number | null;
  nominalPrecision: number | null;
  nominalRecall: number | null;
  nominalTrueNegative: number | null;
  nominalTruePositive: number | null;
  optimalPMInterval: number | null;
  updatedAt: string;
  updatedBy: string;
}

export const getFailureModeRiskModelRequestURI = ({
  id,
  ...params
}: GetFailureModeRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([FAILURE_MODES_BASE_ROUTE, id, "riskModel"]),
    params,
  });

export enum FailureModeEventType {
  PROACTIVE_REPAIR = "Proactive Repair",
  UNCATEGORIZED_REPAIR = "Uncategorized Repair",
  FAILURE = "Failure",
}

export interface ListFailureModesEventsRequest extends APIPaginatedRequest {
  id: string;
  vehiclesFilter?: string;
}

export const getFailureModeEventsCountRequestURI = ({
  id,
  ...params
}: ListFailureModesEventsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([FAILURE_MODES_BASE_ROUTE, id, "events", "count"]),
    params,
  });

export interface FailureModeEventsRepairProactiveRepairIntersectionCountRequest {
  id: string;
}

export const getFailureModeEventsRepairProactiveRepairIntersectionCountRequestURI =
  ({
    id,
  }: FailureModeEventsRepairProactiveRepairIntersectionCountRequest): string =>
    client.getUri({
      method: "get",
      url: createURL([
        FAILURE_MODES_BASE_ROUTE,
        id,
        "events",
        "eventsRepairProactiveRepairIntersection",
        "count",
      ]),
    });

export interface APIListValuesFailureModeEventRequest extends APIListValuesRequest {
  id: string;
}
export const listFailureModeEventsValues = (
  args: APIListValuesFailureModeEventRequest
) =>
  getFetcher<APIListValuesResponse>(
    listFailureModeEventsValuesRequestURI(args)
  );

const listFailureModeEventsValuesRequestURI = ({
  fieldName,
  id,
  ...params
}: APIListValuesFailureModeEventRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["failureModes", id, "events", "values", fieldName]),
    params,
  });

interface APIListValuesFailureModeRiskModelPredictionRequest extends APIListValuesRequest {
  id: string;
}

export const listFailureModeRiskModelPredictionsValues = (
  args: APIListValuesFailureModeRiskModelPredictionRequest
) =>
  getFetcher<APIListValuesResponse>(
    listFailureModeRiskModelPredictionsValuesRequestURI(args)
  );

const listFailureModeRiskModelPredictionsValuesRequestURI = ({
  fieldName,
  id,
  ...params
}: APIListValuesFailureModeEventRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      "failureModes",
      id,
      "riskModelPredictions",
      "values",
      fieldName,
    ]),
    params,
  });

export interface APIListValuesVehicleRiskModelPredictionRequest extends APIListValuesRequest {
  vin: string;
}

export const listVehicleRiskModelPredictionsValues = (
  args: APIListValuesVehicleRiskModelPredictionRequest
) =>
  getFetcher<APIListValuesResponse>(
    listVehicleRiskModelPredictionsValuesRequestURI(args)
  );

const listVehicleRiskModelPredictionsValuesRequestURI = ({
  fieldName,
  vin,
  ...params
}: APIListValuesVehicleRiskModelPredictionRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      VEHICLES_BASE_URL,
      vin,
      "riskModelPredictions",
      "values",
      fieldName,
    ]),
    params,
  });

const listRiskModelPredictionsValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL(["riskModelPredictions", "values", fieldName]),
    params,
  });

export const listRiskModelPredictionsValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(
    listRiskModelPredictionsValuesRequestURI(args)
  );

export interface FailureModeEventSource {
  ID: string;
  type: string;
  timestamp: string;
  mileage: number | null;
  mileageUnit: MileageUnit | null;
}

export interface FailureModeEvent {
  ID: string;
  VIN: string;
  date: string;
  type: FailureModeEventType;
  sources: FailureModeEventSource[];
}

export const listFailureModeEventsRequestURI = ({
  id,
  ...params
}: ListFailureModesEventsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([FAILURE_MODES_BASE_ROUTE, id, "events"]),
    params,
  });

export interface FailureModeEventsTimelineBucket {
  month: string;
  proactiveRepairs: number;
  failures: number;
  totalRepairs: number;
  proactiveRepairsPTV: number;
  failuresPTV: number;
  uncategorizedRepairs: number;
  uncategorizedRepairsPTV: number;
  totalRepairsPTV: number;
}

export interface FailureModeEventsTimeline {
  monthlyCounts: FailureModeEventsTimelineBucket[];
  totalCounts: {
    proactiveRepairs: number;
    failures: number;
    totalRepairs: number;
    proactiveRepairsPTV: number;
    failuresPTV: number;
    totalRepairsPTV: number;
    totalVehicles: number;
    uncategorizedRepairs: number;
    uncategorizedRepairsPTV: number;
  };
}

export const getFailureModeEventsTimelineURI = ({
  id,
  ...params
}: ListFailureModesEventsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([FAILURE_MODES_BASE_ROUTE, id, "eventsTimeline"]),
    params,
  });

export interface ListFailureModesPredictionsRequest extends APIPaginatedRequest {
  id: string;
  vehiclesFilter?: string;
  mileageUnit: MileageUnit;
}

export interface ListFailureModesPredictionsCountRequest extends Omit<
  ListFailureModesPredictionsRequest,
  "mileageUnit"
> {}

export interface ListVehicleRiskModelPredictionsRequest extends APIPaginatedRequest {
  vin: string;
}

export interface RiskModelPrediction {
  VIN: string;
  failureModeID: string;
  riskStatus: string;
  riskScore: string;
  riskLabel: RiskLabel;
  repairDetails: string | null;
  repairByDate: string | null;
  vehicle: Vehicle | null;
  failureMode: FailureMode | null;
}

export const listFailureModeRiskModelPredictionsRequestURI = ({
  id,
  ...params
}: ListFailureModesEventsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([FAILURE_MODES_BASE_ROUTE, id, "riskModelPredictions"]),
    params,
  });

export const listVehicleRiskModelPredictionsRequestURI = ({
  vin,
  ...params
}: ListVehicleRiskModelPredictionsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([VEHICLES_BASE_URL, vin, "riskModelPredictions"]),
    params,
  });

export const listVehicleRiskModelPredictionsCountRequestURI = ({
  vin,
  ...params
}: ListVehicleRiskModelPredictionsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([VEHICLES_BASE_URL, vin, "riskModelPredictions", "count"]),
    params,
  });

export const listFailureModeRiskModelPredictionsCountRequestURI = ({
  id,
  ...params
}: ListFailureModesEventsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      FAILURE_MODES_BASE_ROUTE,
      id,
      "riskModelPredictions",
      "count",
    ]),
    params,
  });

export const getFailureModeRiskModelPredictionsExport = (args: ExportRequest) =>
  getFetcher<Blob>(getFailureModeRiskModelPredictionsRequestURI(args), {
    responseType: "blob",
  });

const getFailureModeRiskModelPredictionsRequestURI = ({
  IDs,
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      FAILURE_MODES_BASE_ROUTE,
      ...IDs,
      "riskModelPredictions",
      "export",
      type,
    ]),
    params,
  });

export const getVehicleRiskModelPredictionsExport = (args: ExportRequest) =>
  getFetcher<Blob>(getVehicleRiskModelPredictionsRequestURI(args), {
    responseType: "blob",
  });

const getVehicleRiskModelPredictionsRequestURI = ({
  IDs,
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      VEHICLES_BASE_URL,
      ...IDs,
      "riskModelPredictions",
      "export",
      type,
    ]),
    params,
  });

export const getFailureModeEventsExport = (args: ExportRequest) =>
  getFetcher<Blob>(getFailureModeEventsRequestURI(args), {
    responseType: "blob",
  });

const getFailureModeEventsRequestURI = ({
  IDs,
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      FAILURE_MODES_BASE_ROUTE,
      ...IDs,
      "events",
      "export",
      type,
    ]),
    params,
  });

// ---------
// Survival curves
// ---------

export interface SurvivalCurveResponse {
  failure99Percentile: number | null;
  failureAverage: number | null;
  distances: number[];
  probabilities: number[];
}

export interface GetSurvivalCurvesRequest {
  failureModeID: string;
  mileageUnit: MileageUnit;
}

export interface ListSimilarVehiclesRequest extends APIPaginatedRequest {
  currentVIN: string;
  failureModeID: string;
}

export const listSimilarVehiclesRequestURI = ({
  failureModeID,
  currentVIN,
  ...params
}: ListSimilarVehiclesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      "failureModes",
      failureModeID,
      "similarVehicles",
      currentVIN,
    ]),
    params,
  });

export interface SimilarVehicle {
  currentVIN: string;
  similarVIN: string;
  failureModeID: string;
  date: string;
  similarity: number;
}

export interface SimilarVehicleFactors {
  target: string;
  currentVIN: string;
  similarVIN: string;
  factors: SimilarVehicleFactor[];
}

interface SimilarVehicleFactor {
  name: string;
  description: string;
  currentVINValue: number;
  similarVINValue: number;
  currentVINPercentile: number;
  similarVINPercentile: number;
}

export interface ListSimilarVehicleFactorsRequest {
  failureModeID: string;
  currentVIN: string;
  similarVIN: string;
  limit?: number;
}

export const listSimilarVehicleFactorsRequestURI = ({
  failureModeID,
  currentVIN,
  similarVIN,
}: ListSimilarVehicleFactorsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      "failureModes",
      failureModeID,
      "similarVehicles",
      currentVIN,
      "factors",
      similarVIN,
    ]),
  });

export interface FailureModeRequest {
  name: string;
}

const newFailureModeRequestURI = (): string =>
  client.getUri({
    method: "POST",
    url: FAILURE_MODES_BASE_ROUTE,
  });

export const newFailureMode = (data: FailureModeRequest) =>
  postFetcher<FailureMode>(newFailureModeRequestURI(), data);

const deleteFailureModeRequestURI = ({ id }: DeleteRequest): string =>
  client.getUri({
    method: "DELETE",
    url: createURL([FAILURE_MODES_BASE_ROUTE, id]),
  });

export const deleteFailureMode = (args: DeleteRequest) =>
  client.delete(deleteFailureModeRequestURI(args));

interface UpdateFailureModeRequest extends Partial<FailureModeRequest> {
  ID: string;
}

const updateFailureModeRequestURI = ({
  ID,
  ...params
}: UpdateFailureModeRequest): string =>
  client.getUri({
    method: "PUT",
    url: createURL([FAILURE_MODES_BASE_ROUTE, ID]),
    params,
  });

export const updateFailureMode = ({ ID, ...args }: UpdateFailureModeRequest) =>
  patchFetcher<FailureMode>(updateFailureModeRequestURI({ ID }), args);
